import {
    BookingCheckoutState,
    DateBookingRequestCheckoutState,
    GroupBookingRequestCheckoutState,
    CheckoutState,
    OrderCheckoutState,
    RequestCheckoutState,
    BookingApplicationCheckoutState,
} from '@shared/types';
import { PublicCourse, PublicEducator } from '@cademy-package/sdk-component-types';
import { ReactNode } from 'react';
import { ResponseSubmitter } from '../useCheckout';
import { BookingFlow } from './BookingFlow';
import { BookingCheckoutContextProvider } from '../CheckoutContext/BookingContext';
import { CademyError } from '@shared/domain-shared';
import { OrderCheckoutContextProvider } from '../CheckoutContext/OrderContext';
import { OrderFlow } from './OrderFlow';
import { RequestCheckoutContextProvider } from '../CheckoutContext/RequestContext';
import { RequestFlow } from './RequestFlow';
import { BookingRequestCheckoutContextProvider } from '../CheckoutContext/BookingRequestContext';
import { BookingRequestFlow } from './BookingRequestFlow';
import { BookingApplicationCheckoutContextProvider } from '../CheckoutContext/BookingApplicationContext';
import { BookingApplicationFlow } from './BookingApplicationFlow';

export type CheckoutFlowProps<TCheckoutState extends CheckoutState> = {
    course: PublicCourse;
    educator: PublicEducator;
    checkoutState: TCheckoutState;
    submitResponse: ResponseSubmitter<TCheckoutState>;
    isLoading: boolean;
    error: CademyError | null;
};

export const CheckoutFlow = <TCheckoutState extends CheckoutState>({
    course,
    educator,
    checkoutState,
    submitResponse,
    isLoading,
    error,
}: CheckoutFlowProps<TCheckoutState>): NonNullable<ReactNode> => {
    switch (checkoutState.flow) {
        case 'booking': {
            if (course.type !== 'class' && course.type !== 'blended') {
                throw new Error(`Invalid course type "${course.type}" for booking flow`);
            }
            return (
                <BookingCheckoutContextProvider
                    course={course}
                    educator={educator}
                    checkoutState={checkoutState}
                    submitResponse={submitResponse as ResponseSubmitter<BookingCheckoutState>}
                    isLoading={isLoading}
                    error={error}
                >
                    <BookingFlow />
                </BookingCheckoutContextProvider>
            );
        }
        case 'order': {
            if (course.type !== 'on-demand') {
                throw new Error(`Invalid course type "${course.type}" for order flow`);
            }
            return (
                <OrderCheckoutContextProvider
                    course={course}
                    educator={educator}
                    checkoutState={checkoutState}
                    submitResponse={submitResponse as ResponseSubmitter<OrderCheckoutState>}
                    isLoading={isLoading}
                    error={error}
                >
                    <OrderFlow />
                </OrderCheckoutContextProvider>
            );
        }
        case 'request': {
            if (course.type !== 'on-request') {
                throw new Error(`Invalid course type "${course.type}" for request flow`);
            }

            return (
                <RequestCheckoutContextProvider
                    course={course}
                    educator={educator}
                    checkoutState={checkoutState}
                    submitResponse={submitResponse as ResponseSubmitter<RequestCheckoutState>}
                    isLoading={isLoading}
                    error={error}
                >
                    <RequestFlow />
                </RequestCheckoutContextProvider>
            );
        }
        case 'dateBookingRequest':
        case 'groupBookingRequest': {
            if (course.type !== 'class' && course.type !== 'blended') {
                throw new Error(
                    `Invalid course type "${course.type}" for ${checkoutState.flow} flow`
                );
            }
            return (
                <BookingRequestCheckoutContextProvider
                    course={course}
                    educator={educator}
                    checkoutState={checkoutState}
                    submitResponse={
                        submitResponse as ResponseSubmitter<
                            DateBookingRequestCheckoutState | GroupBookingRequestCheckoutState
                        >
                    }
                    isLoading={isLoading}
                    error={error}
                >
                    <BookingRequestFlow />
                </BookingRequestCheckoutContextProvider>
            );
        }
        case 'bookingApplication': {
            if (course.type !== 'class' && course.type !== 'blended') {
                throw new Error(`Invalid course type "${course.type}" for bookingApplication flow`);
            }
            return (
                <BookingApplicationCheckoutContextProvider
                    course={course}
                    educator={educator}
                    checkoutState={checkoutState}
                    submitResponse={
                        submitResponse as ResponseSubmitter<BookingApplicationCheckoutState>
                    }
                    isLoading={isLoading}
                    error={error}
                >
                    <BookingApplicationFlow />
                </BookingApplicationCheckoutContextProvider>
            );
        }
    }
};
